import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Bell } from 'lucide-react';
import { t, getLocalizedString } from '@/i18n/translations';
import { Habit, HabitCustomization, Language, ReminderType } from '@/types';
import { useStore } from '@/store/useStore';

const DAY_ORDER = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

interface HabitEditModalProps {
  habit: Habit;
  customization: HabitCustomization | undefined;
  lang: Language;
  onSave: (customization: HabitCustomization) => void;
  onClose: () => void;
}

export default function HabitEditModal({
  habit,
  customization,
  lang,
  onSave,
  onClose,
}: HabitEditModalProps) {
  const { settings } = useStore();
  const [isInitialized, setIsInitialized] = useState(false);

  // Use ref for onSave to avoid infinite loop in auto-save
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const defaultDays = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
  const habitDays = habit.days || defaultDays;
  const initialTargetTime = customization?.targetTime !== undefined ? customization.targetTime : habit.targetTime;

  const [days, setDays] = useState<string[]>(customization?.days || habitDays);
  const [targetTime, setTargetTime] = useState<string | null>(initialTargetTime);
  const [useTime, setUseTime] = useState(initialTargetTime !== null);
  const [oneTime, setOneTime] = useState(customization?.oneTime ?? habit.oneTime ?? false);
  const [reminder, setReminder] = useState<ReminderType>(customization?.reminder ?? 'none');
  const [reminderTime, setReminderTime] = useState<string>(
    customization?.reminderTime ?? settings.reminderTime
  );
  const isOriginallyOneTime = habit.oneTime ?? false;

  // Reset state when habit changes
  useEffect(() => {
    const currentHabitDays = habit.days || defaultDays;
    const newTargetTime = customization?.targetTime !== undefined ? customization.targetTime : habit.targetTime;
    setDays(customization?.days || currentHabitDays);
    setTargetTime(newTargetTime);
    setUseTime(newTargetTime !== null);
    setOneTime(customization?.oneTime ?? habit.oneTime ?? false);
    setReminder(customization?.reminder ?? 'none');
    setReminderTime(customization?.reminderTime ?? settings.reminderTime);
    setIsInitialized(false);
    // Small delay to allow state to settle before enabling auto-save
    const timer = setTimeout(() => setIsInitialized(true), 50);
    return () => clearTimeout(timer);
  }, [habit.id]);

  const handleDayToggle = (day: string) => {
    if (days.includes(day)) {
      setDays(days.filter((d) => d !== day));
    } else {
      setDays([...days, day]);
    }
  };

  // Auto-save on any change (only after initialized)
  const saveChanges = useCallback(() => {
    if (!isInitialized) return;
    const currentHabitDays = habit.days || defaultDays;
    onSaveRef.current({
      days: days.length === currentHabitDays.length && days.every((d) => currentHabitDays.includes(d))
        ? undefined
        : days,
      targetTime: useTime ? targetTime : null,
      oneTime: oneTime !== isOriginallyOneTime ? oneTime : undefined,
      reminder: reminder !== 'none' ? reminder : undefined,
      reminderTime: !useTime && reminder !== 'none' ? reminderTime : undefined,
    });
  }, [isInitialized, days, targetTime, useTime, oneTime, reminder, reminderTime, habit.days, isOriginallyOneTime]);

  // Save on every change
  useEffect(() => {
    saveChanges();
  }, [saveChanges]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-xl mb-4 sm:mb-0">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">{habit.icon}</span>
            <span className="font-semibold text-gray-900">
              {getLocalizedString(habit.name, lang)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-6">
          {/* One-time toggle at top - always show toggle for all habits */}
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            <button
              onClick={() => setOneTime(false)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                !oneTime
                  ? 'bg-primary-500 text-white'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('tracker.recurring', lang)}
            </button>
            <button
              onClick={() => setOneTime(true)}
              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                oneTime
                  ? 'bg-amber-500 text-white'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t('tracker.oneTime', lang)}
            </button>
          </div>

          {/* Schedule, Time, Reminder - only show for recurring habits */}
          {!oneTime && (
            <>
              {/* Days */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tracker.schedule', lang)}
                </label>
                <div className="flex gap-1">
                  {DAY_ORDER.map((day) => (
                    <button
                      key={day}
                      onClick={() => handleDayToggle(day)}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                        days.includes(day)
                          ? 'bg-primary-500 text-white'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      {t(`day.${day}` as any, lang)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('tracker.time', lang)}
                </label>
                <div className="flex rounded-lg overflow-hidden border border-gray-200">
                  <button
                    onClick={() => setUseTime(false)}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                      !useTime
                        ? 'bg-gray-600 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {t('tracker.noTime', lang)}
                  </button>
                  <button
                    onClick={() => {
                      setUseTime(true);
                      if (!targetTime) {
                        setTargetTime('09:00');
                      }
                    }}
                    className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                      useTime
                        ? 'bg-primary-500 text-white'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {lang === 'nl' ? 'Specifiek tijdstip' : lang === 'fr' ? 'Heure précise' : 'Specific time'}
                  </button>
                </div>
                {useTime && (
                  <div className="mt-3">
                    <input
                      type="time"
                      value={targetTime || '09:00'}
                      onChange={(e) => setTargetTime(e.target.value)}
                      className="w-full bg-gray-100 rounded-lg px-3 py-2.5 text-gray-700 text-center text-lg"
                    />
                  </div>
                )}
              </div>

              {/* Reminder for timed habits */}
              {useTime && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Bell className="w-4 h-4" />
                    {t('tracker.reminder', lang)}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['none', 'before', 'at', 'after'] as ReminderType[]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setReminder(type)}
                        className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                          reminder === type
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {t(`tracker.reminder${type.charAt(0).toUpperCase() + type.slice(1)}` as any, lang)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Reminder for all-day habits */}
              {!useTime && (
                <div>
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                    <Bell className="w-4 h-4" />
                    {t('tracker.reminder', lang)}
                  </label>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={reminder !== 'none'}
                        onChange={(e) => setReminder(e.target.checked ? 'at' : 'none')}
                        className="w-4 h-4 text-primary-500 rounded"
                      />
                    </label>
                    <input
                      type="time"
                      value={reminderTime}
                      onChange={(e) => setReminderTime(e.target.value)}
                      disabled={reminder === 'none'}
                      className={`bg-gray-100 rounded-lg px-3 py-2 text-gray-700 ${
                        reminder === 'none' ? 'opacity-50' : ''
                      }`}
                    />
                  </div>
                </div>
              )}
            </>
          )}

        </div>

        {/* Reset to default */}
        <div className="px-4 pb-4">
          <button
            onClick={() => {
              setDays(habit.days || defaultDays);
              setTargetTime(habit.targetTime);
              setUseTime(habit.targetTime !== null);
              setOneTime(habit.oneTime ?? false);
              setReminder('none');
              setReminderTime(settings.reminderTime);
            }}
            className="w-full text-sm text-gray-500 hover:text-gray-700 py-2 transition-colors"
          >
            {t('tracker.resetToDefault', lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
